import { Modal } from '@eventbrite/eds-modal';
import { Typography } from '@eventbrite/marmalade';
import React, { useEffect } from 'react';

import { useWindowSize } from 'react-use';
import { MODAL_FORM_COMPLETE_BUTTON_TEXT } from '../../constants';
import {
    HeapActionProps,
    SalesEligibleModalProps,
    SubmitFormProps,
} from '../../types';
import { getCohortModalContent, trackHeapEvent } from '../../utils';
import {
    FeatureList,
    SalesEligibleComplete,
    SalesEligibleModalForm,
} from './components';
import './SalesEligibleModal.scss';

const MOBILE_BREAKPOINT = 660;

export const SalesEligibleModal: React.FunctionComponent<
    SalesEligibleModalProps
> = ({
    cohort,
    entryPoint,
    handleClose,
    handleComplete,
    updateUserContextFromSalesEligible,
}) => {
    const [isComplete, setIsComplete] = React.useState(false);

    const { width } = useWindowSize();
    const isMobile = width < MOBILE_BREAKPOINT;

    useEffect(() => {
        if (!isComplete) {
            trackHeapEvent(
                HeapActionProps.SalesEligibleModalShown,
                entryPoint,
                cohort,
            );
        }
    });

    const { title: titleText, description: descriptionText } =
        getCohortModalContent(cohort);

    const submittedForm = (formData: SubmitFormProps) => {
        if (updateUserContextFromSalesEligible) {
            updateUserContextFromSalesEligible(formData);
        }
        trackHeapEvent(
            HeapActionProps.SalesEligibleModalSubmitted,
            entryPoint,
            cohort,
        );
        setIsComplete(true);
    };

    return (
        <Modal
            __containerClassName="sales-eligible-modal-container"
            isShown
            onClose={isComplete ? handleComplete : handleClose}
            onClickOutside={isComplete ? handleComplete : handleClose}
            noPadding
            onPrimaryAction={isComplete ? handleComplete : handleClose}
            primaryText={isComplete && MODAL_FORM_COMPLETE_BUTTON_TEXT}
            primaryType={isComplete ? 'neutral' : undefined}
            aria-label="Sales Eligible Modal"
        >
            <div
                role="main"
                aria-label="Sales Eligible Onboarding Form"
                className="sales-eligible-modal-main"
            >
                {isComplete && <SalesEligibleComplete />}
                {!isComplete && (
                    <div
                        data-testid="sales-eligible-modal"
                        className="sales-eligible-modal"
                    >
                        <div className="sales-eligible-modal__form-container">
                            <Typography
                                variant="heading-sm"
                                color="neutral-900"
                                className="sales-eligible-modal__form-title"
                            >
                                {titleText}
                            </Typography>
                            <Typography
                                variant="body-md"
                                color="neutral-900"
                                className="sales-eligible-modal__form-description"
                            >
                                <span
                                    //eslint-disable-next-line react/no-danger
                                    dangerouslySetInnerHTML={{
                                        __html: descriptionText.toString(),
                                    }}
                                />
                            </Typography>
                            {!isMobile && (
                                <SalesEligibleModalForm
                                    entryPoint={entryPoint}
                                    cohort={cohort}
                                    submittedForm={submittedForm}
                                />
                            )}
                        </div>
                        <div className="sales-eligible-modal__feature-container">
                            <FeatureList />
                        </div>
                        {isMobile && (
                            <div className="sales-eligible-modal__form-mobile-container">
                                <SalesEligibleModalForm
                                    entryPoint={entryPoint}
                                    cohort={cohort}
                                    submittedForm={submittedForm}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};
